/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { forwardRef, useRef, useEffect, useContext, useState } from 'react';
import { useCallbackOne } from 'use-memo-one';
import { css, jsx } from '@compiled/react';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { useMergeRefs } from 'use-callback-ref';
import { createIframeBridge } from '@atlassian/bridge-core/v2';
import type { HTMLCustomIFrameElement, InnerIframeProps } from '../types';
import { EnvironmentContext } from '../../../context';
import { useInitialAppState } from './useInitialAppState';
import { useRuntimeOverlayController } from '../../../worker-runtime/useRuntimeOverlayController';
import { useBridgeServices } from '../../../bridge/useBridgeServices';
import type { OverlayController, BridgeControls } from '../../../bridge/types';
import { getLoadingComponent } from '../utils/getLoadingComponent';
import { useBridgeContext } from '../../../bridge/useBridgeContext';
import { createBridgeFeatures } from '../../../bridge/createBridgeFeatures';
import { egressPermissionService } from '../../../utils/useEgressPermissionService';
import type { GQLExtension } from '../../../web-client/graphql/types';

const styles = cssMap({
	box: {
		position: 'relative',
	},
	loadingOverlay: {
		width: '100%',
		height: '100%',
	},
});

// TODO: no styles defined for resizable modals yet.
// Only Jira enables resizing on modals at the moment.
const modalIframeStyles = css({
	borderRadius: token('radius.small', '3px'),
	height: '100%',
});

const iframeStyles = css({
	border: 'none',
	display: 'block',
	width: '100%',
});

// We intentionally don't use `display: none` to hide the iframe, as some browsers (e.g. Safari) can suspend
// script execution in iframes that are not rendered, which would break the app initialization and bridge connection.
// Instead, we use `visibility: hidden` to keep the iframe active but invisible.
const hiddenIframeStyles = css({
	visibility: 'hidden',
});

const nonResizableIframeStyles = css({
	height: '100%',
});

// All permissions granted unconditionally — this is a fresh implementation with no legacy rollout gates.
const FRAME_PERMISSIONS_POLICY =
	'camera; clipboard-write; display-capture; fullscreen; microphone; local-network-access';

// Base sandbox rules always applied to every hosted-resource iframe.
const BASE_SANDBOX_RULES = [
	'allow-downloads',
	'allow-forms',
	'allow-modals',
	'allow-pointer-lock',
	'allow-same-origin',
	'allow-scripts',
] as const;

/**
 * Returns the sandbox attribute string for the iframe.
 * Apps with a wildcard egress rule are granted popup permissions so they can
 * open third-party URLs in new tabs/windows.
 */
const getSandboxAttribute = (egress: GQLExtension['egress']): string => {
	const rules: string[] = [...BASE_SANDBOX_RULES];
	try {
		if (egressPermissionService('FETCH_CLIENT_SIDE', egress).containsWildCardEgress()) {
			rules.push('allow-popups', 'allow-popups-to-escape-sandbox');
		}
		// eslint-disable-next-line no-unused-vars
	} catch (_) {
		// swallow error — treat as no wildcard egress
	}
	return rules.join(' ');
};

export const InnerIframe: React.ForwardRefExoticComponent<
	InnerIframeProps & React.RefAttributes<HTMLCustomIFrameElement>
> = forwardRef<HTMLCustomIFrameElement, InnerIframeProps>(
	(
		{
			accountId,
			apolloClient,
			appId,
			bridge,
			components,
			contextIds,
			coreData,
			extension,
			extensionData,
			extensionPayload,
			extensionViewData,
			getContextToken,
			height = '100%',
			isHidden,
			isInModal,
			isResizable,
			loadingComponent,
			locale,
			onLoad,
			src,
			setIsLoading,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			surfaceColor,
			theme,
			timezone,
			width = '100%',
		},
		parentIframeRef,
	) => {
		const { overlay, overlayController, isInlineActive } = useRuntimeOverlayController();

		const { userConsentRequired, renderThreeLOPrompt } = useInitialAppState({
			extension,
			appId,
			accountId,
			components,
			onLoad,
			setIsThreeLOPromptVisible,
			setShouldShowRenderer,
			overlayController,
		});

		const {
			getBridgeFeatures,
			destroy: cleanupBridge,
			setBridgeControl,
		} = useBridgeFeatures(
			{
				accountId,
				apolloClient,
				appId,
				bridge,
				contextIds,
				coreData,
				extension,
				extensionData,
				extensionPayload,
				extensionViewData,
				getContextToken,
				locale,
				surfaceColor,
				theme,
				timezone,
			},
			overlayController,
			renderThreeLOPrompt,
		);

		const localIframeRef = useRef<HTMLCustomIFrameElement>(null);
		const iframeRef = useMergeRefs([parentIframeRef, localIframeRef]);

		const iframeTitle = useIframeTitle(extension.properties.frameId, appId);

		const { handleIframeLoad, isBridgeReady } = useIframeBridgeConnection({
			iframeRef: localIframeRef,
			src,
			theme,
			surfaceColor,
			getBridgeFeatures,
			cleanupBridge,
			onLoad,
			setBridgeControl,
		});

		const loading = !isBridgeReady;

		useEffect(() => {
			setIsLoading?.(loading);
		}, [loading, setIsLoading]);

		const sandbox = getSandboxAttribute(extension.egress);

		return (
			<Box
				xcss={styles.box}
				style={{
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					height: isResizable || isInlineActive ? undefined : height,
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
					width,
				}}
			>
				<IframeLoadingOverlay
					loading={loading}
					isInlineActive={isInlineActive}
					isHidden={isHidden}
					loadingComponent={loadingComponent}
				/>
				{overlay}
				{!userConsentRequired && (
					<iframe
						data-testid="hosted-resources-iframe"
						data-forge-iframe
						css={[
							iframeStyles,
							(loading || isInlineActive || isHidden) && hiddenIframeStyles,
							isInModal && modalIframeStyles,
							!isResizable && nonResizableIframeStyles,
						]}
						sandbox={sandbox}
						allow={FRAME_PERMISSIONS_POLICY}
						src={src}
						ref={iframeRef}
						onLoad={handleIframeLoad}
						title={iframeTitle}
					/>
				)}
			</Box>
		);
	},
);

type IframeLoadingOverlayProps = {
	isHidden: InnerIframeProps['isHidden'];
	isInlineActive: boolean;
	loading: boolean;
	loadingComponent: InnerIframeProps['loadingComponent'];
};

/**
 * Renders a loading placeholder while the bridge handshake is in progress.
 *
 * Suppressed in two cases where it would be redundant or misleading:
 * - `isInlineActive`: an interactive overlay (e.g. ThreeLOPrompt) is occupying the slot —
 *   the user is already looking at meaningful UI, so the spinner would be noise underneath it.
 * - `isHidden`: UIKit apps pass `isHidden={true}` — their visible content is rendered by `RendererNext` in the
 *   host, which owns its own loading state. Showing a spinner here would be a duplicate.
 */
const IframeLoadingOverlay = ({
	loading,
	isInlineActive,
	isHidden,
	loadingComponent,
}: IframeLoadingOverlayProps) => {
	if (!loading || isInlineActive || isHidden) {
		return null;
	}
	return <Box xcss={styles.loadingOverlay}>{getLoadingComponent(loadingComponent)}</Box>;
};

const useIframeTitle = (frameId: string | undefined, appId: string): string => {
	const idRef = useRef<string | null>(null);
	if (idRef.current === null) {
		idRef.current = crypto.randomUUID();
	}
	return `Iframe ${frameId ?? idRef.current} from ${appId}`;
};

type UseBridgeFeaturesInput = Pick<
	InnerIframeProps,
	| 'accountId'
	| 'apolloClient'
	| 'appId'
	| 'bridge'
	| 'contextIds'
	| 'coreData'
	| 'extension'
	| 'extensionData'
	| 'extensionPayload'
	| 'extensionViewData'
	| 'getContextToken'
	| 'locale'
	| 'surfaceColor'
	| 'theme'
	| 'timezone'
>;

type UseIframeBridgeConnectionInput = {
	/** Tears down the bridge connection and all associated context resources. */
	cleanupBridge: () => void;
	getBridgeFeatures: () => ReturnType<typeof createBridgeFeatures>;
	iframeRef: React.RefObject<HTMLCustomIFrameElement>;
	onLoad: InnerIframeProps['onLoad'];
	setBridgeControl: (controls: BridgeControls) => void;
	src: string;
	surfaceColor: InnerIframeProps['surfaceColor'];
	theme: InnerIframeProps['theme'];
};

/**
 * Manages the bridge-core v2 connection lifecycle for a hosted-resource iframe.
 *
 * Responsibilities:
 * - Opens {@link createIframeBridge} when the iframe fires its `load` event.
 * - Tears down the bridge and bridge context on unmount.
 *
 * The iframe `src` is set once and never changed. Guest-side navigation is also
 * not supported (the bridge setup script is injected into the app's index.html
 * at build time and cannot reinitialise). `handleIframeLoad` therefore fires
 * exactly once per component lifetime; a second invocation is an error.
 */
const useIframeBridgeConnection = ({
	iframeRef,
	src,
	theme,
	surfaceColor,
	getBridgeFeatures,
	cleanupBridge,
	onLoad,
	setBridgeControl,
}: UseIframeBridgeConnectionInput): { handleIframeLoad: () => void; isBridgeReady: boolean } => {
	const [isBridgeReady, setIsBridgeReady] = useState(false);

	// Guards against storing controls when the component has already been destroyed.
	const isDestroyedRef = useRef(false);

	// Stable handler — src, getBridgeFeatures, and setBridgeControl are all stable references,
	// so this callback is recreated only if theme, surfaceColor, or onLoad change (rare near mount).
	const handleIframeLoad = useCallbackOne(() => {
		const contentWindow = iframeRef.current?.contentWindow;
		if (!contentWindow) {
			return;
		}

		createIframeBridge({
			withWindow: contentWindow,
			withDomain: new URL(src).origin,
			features: getBridgeFeatures(),
			theme,
			surfaceColor,
			sendHandshakeMetric: () => {
				// TODO: implement proper metrics
			},
		})
			.open()
			.then((controls) => {
				if (isDestroyedRef.current) {
					// Component was destroyed while open() was in-flight — close immediately.
					controls.close();
				} else {
					setBridgeControl(controls);
					setIsBridgeReady(true);
					onLoad?.();
				}
			})
			.catch((err) => {
				// eslint-disable-next-line no-console
				console.error('Failed to open iframe bridge', err);
			});
	}, [src, theme, surfaceColor, getBridgeFeatures, onLoad, setBridgeControl]);

	// Tear down bridge and bridge context on unmount.
	// destroy() (called inside cleanupBridge) closes liveControls — no explicit call needed.
	useEffect(() => {
		return () => {
			isDestroyedRef.current = true;
			cleanupBridge();
		};
	}, [cleanupBridge]);

	return { handleIframeLoad, isBridgeReady };
};

const useBridgeFeatures = (
	props: UseBridgeFeaturesInput,
	overlayController: OverlayController,
	renderThreeLOPrompt: (authUrl: string, retry: () => Promise<void>) => Promise<void>,
) => {
	const environment = useContext(EnvironmentContext);
	// Assembles host-side capabilities (invoker, fetch client, tracing, etc.) for bridge methods.
	// Returns a getter rather than a snapshot so services are always current at call time.
	const { getServices } = useBridgeServices(props, overlayController, renderThreeLOPrompt);

	// [Important] getBridgeContext is stable and does not change across the lifecycle of the component,
	// so it is safe to include it in the dependency array of createBridgeFeatures without causing
	// unnecessary re-renders or re-creations of the bridge features.
	const { getBridgeContext, destroy, onChange, setBridgeControl } = useBridgeContext(
		{ ...props, environment },
		getServices,
	);

	// Push theme/surfaceColor changes to the open bridge connection whenever they change.
	// Registered once — `onChange` is stable and `destroy()` clears the listener on unmount.
	useEffect(() => {
		return onChange((prev, next, getContext) => {
			if (prev.theme !== next.theme || prev.surfaceColor !== next.surfaceColor) {
				getContext().bridgeControls?.setTheme(
					next.theme ?? null,
					next.surfaceColor ?? null,
					next.environment,
				);
			}
		});
	}, [onChange]);

	return {
		destroy,
		getBridgeFeatures: () => createBridgeFeatures(getBridgeContext),
		setBridgeControl,
	};
};
